import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listarTodosPaletes, buscarPaleteDetalhado, exportarRelatorioConferencia
} from '../../../services/montagemPalete';
import { listarClientes } from '../../../services/clientes';
import type {
  PaleteListItem, PaleteDetalhado, PaleteDetalhadoPedido,
  PaleteDetalhadaEtiquetaPendente, PaleteDetalhadoEtiqueta
} from '../../../services/montagemPalete';
import { getHojeISO, toInicioDiaISO, toFimDiaISO } from '../../../utils/dateHelpers';
import { exportToExcel } from '../../../utils/excelExport';
import { imprimirRelatorioConferencia } from '../../../utils/relatorioConferenciaPrint';
import '../../MontagemPaletes/MontagemPaletes.css';

function calcularRemovidosUnicos(etiquetas: { codigo_lido: string; resultado: string }[]): number {
  const codigosOk = new Set<string>();
  const codigosRemovidos = new Set<string>();
  for (const e of etiquetas) {
    if (e.resultado === 'ok' && e.codigo_lido) codigosOk.add(e.codigo_lido);
    if (e.resultado === 'removido' && e.codigo_lido) codigosRemovidos.add(e.codigo_lido);
  }
  let count = 0;
  codigosRemovidos.forEach(c => { if (!codigosOk.has(c)) count++; });
  return count;
}

const STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  em_conferencia: 'Em Conferência',
  em_carregamento: 'Em Carregamento',
  em_transito: 'Em Trânsito',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

const STATUS_PALETE_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  pre_carregamento: 'Pré-Carreg.',
  carregado: 'Carregado',
  em_transito: 'Em Trânsito',
  descarregado: 'Descarregado',
  conferido: 'Conferido',
  inutilizado: 'Inutilizado'
};

const STATUS_PALETE_COLORS: Record<string, { color: string; bg: string }> = {
  pendente: { color: '#0c5460', bg: '#d1ecf1' },
  pre_carregamento: { color: '#856404', bg: '#fff3cd' },
  carregado: { color: '#155724', bg: '#d4edda' },
  em_transito: { color: '#1a5276', bg: '#d4e6f9' },
  descarregado: { color: '#7b241c', bg: '#fadbd8' },
  conferido: { color: '#145a32', bg: '#abebc6' },
  inutilizado: { color: '#721c24', bg: '#f8d7da' }
};

const RESULTADO_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  ok: { label: 'OK', color: '#27ae60', icon: 'fas fa-check-circle' },
  duplicado: { label: 'Duplicado', color: '#e67e22', icon: 'fas fa-clone' },
  removido: { label: 'Removido', color: '#95a5a6', icon: 'fas fa-trash' },
  nao_encontrado: { label: 'Não encontrado', color: '#e74c3c', icon: 'fas fa-times-circle' },
  pendente: { label: 'Pendente', color: '#c0392b', icon: 'fas fa-hourglass-half' },
};

const RelatoriosPage: React.FC = () => {
  const navigate = useNavigate();
  const [paletes, setPaletes] = useState<PaleteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filtros
  const [busca, setBusca] = useState('');
  const [buscaApplied, setBuscaApplied] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroNumeroPedido, setFiltroNumeroPedido] = useState('');
  const [filtroNumeroPedidoApplied, setFiltroNumeroPedidoApplied] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState<string>(getHojeISO());
  const [filtroDataFim, setFiltroDataFim] = useState<string>(getHojeISO());

  const [erro, setErro] = useState('');
  const [clientesOptions, setClientesOptions] = useState<{ id: number; razao_social: string }[]>([]);

  // Expansão hierárquica
  const [expandedPalete, setExpandedPalete] = useState<number | null>(null);
  const [expandedPedido, setExpandedPedido] = useState<string | null>(null);
  const [paleteDetalhado, setPaleteDetalhado] = useState<PaleteDetalhado | null>(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);

  // Estado de exportacao (bloqueia botoes durante export)
  const [exportando, setExportando] = useState<'excel' | 'pdf' | null>(null);

  const carregarPaletes = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, limit: 20 };
      if (buscaApplied) params.busca = buscaApplied;
      if (filtroCliente) params.cliente_id = filtroCliente;
      if (filtroNumeroPedidoApplied.trim()) params.numero_pedido = filtroNumeroPedidoApplied.trim();

      // Busca por identificador desconsidera data e pesquisa todo historico.
      const buscaAtiva = Boolean(
        (buscaApplied && buscaApplied.trim()) || filtroNumeroPedidoApplied.trim()
      );
      const dataInicioParam = buscaAtiva ? undefined : toInicioDiaISO(filtroDataInicio);
      const dataFimParam = buscaAtiva ? undefined : toFimDiaISO(filtroDataFim);
      if (dataInicioParam) params.data_inicio = dataInicioParam;
      if (dataFimParam) params.data_fim = dataFimParam;

      const result = await listarTodosPaletes(params);
      if (result.success) {
        setPaletes(result.paletes);
        setTotalPages(result.pagination.totalPages);
        setTotal(result.pagination.total);
      }
    } catch (error: any) {
      setErro(error?.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [page, buscaApplied, filtroCliente, filtroNumeroPedidoApplied, filtroDataInicio, filtroDataFim]);

  useEffect(() => { carregarPaletes(); }, [carregarPaletes]);

  useEffect(() => {
    listarClientes({ limit: 200, ativo: 'true' }).then(res => {
      if (res.success && res.clientes) {
        setClientesOptions(res.clientes.map((c: any) => ({ id: c.id, razao_social: c.razao_social })));
      }
    }).catch(() => {});
  }, []);

  const handleExpandPalete = async (paleteId: number) => {
    if (expandedPalete === paleteId) {
      setExpandedPalete(null);
      setPaleteDetalhado(null);
      setExpandedPedido(null);
      return;
    }
    setExpandedPalete(paleteId);
    setExpandedPedido(null);
    setLoadingDetalhe(true);
    try {
      // Detalhes seguem o mesmo filtro de periodo da lista: quando ha busca
      // por identificador, ignora data; senao, aplica data_inicio/data_fim
      // para consistencia entre lista e detalhes.
      const buscaAtiva = Boolean(
        (buscaApplied && buscaApplied.trim()) || filtroNumeroPedidoApplied.trim()
      );
      const result = await buscarPaleteDetalhado(paleteId, {
        data_inicio: buscaAtiva ? undefined : toInicioDiaISO(filtroDataInicio),
        data_fim: buscaAtiva ? undefined : toFimDiaISO(filtroDataFim)
      });
      if (result.success) {
        setPaleteDetalhado(result.palete);
      }
    } catch (err: any) {
      setErro(err?.message || 'Erro ao carregar detalhes do palete');
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const handleExpandPedido = (numeroPedido: string) => {
    setExpandedPedido(expandedPedido === numeroPedido ? null : numeroPedido);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const aplicarFiltros = () => {
    setBuscaApplied(busca);
    setFiltroNumeroPedidoApplied(filtroNumeroPedido);
    setPage(1);
  };

  const buscarDadosExport = async () => {
    const buscaAtiva = Boolean(
      (buscaApplied && buscaApplied.trim()) || filtroNumeroPedidoApplied.trim()
    );
    const result = await exportarRelatorioConferencia({
      busca: buscaApplied || undefined,
      numero_pedido: filtroNumeroPedidoApplied.trim() || undefined,
      cliente_id: filtroCliente || undefined,
      data_inicio: buscaAtiva ? undefined : toInicioDiaISO(filtroDataInicio),
      data_fim: buscaAtiva ? undefined : toFimDiaISO(filtroDataFim)
    });
    if (!result.success) {
      throw new Error('Nao foi possivel gerar o relatorio');
    }
    return result.dados;
  };

  const handleExportarExcel = async () => {
    try {
      setExportando('excel');
      setErro('');
      const dados = await buscarDadosExport();
      if (dados.length === 0) {
        const buscaAtiva = Boolean(
          (buscaApplied && buscaApplied.trim()) || filtroNumeroPedidoApplied.trim()
        );
        setErro(buscaAtiva
          ? 'Nenhum dado encontrado para os filtros de busca atuais.'
          : `Nenhuma leitura encontrada no periodo de ${filtroDataInicio} a ${filtroDataFim}. Tente ampliar o intervalo de datas.`);
        return;
      }
      const nomeCliente = filtroCliente
        ? (clientesOptions.find(c => String(c.id) === String(filtroCliente))?.razao_social || '')
        : '';
      exportToExcel({
        data: dados,
        columns: [
          { header: 'Nº Palete', key: 'numero_palete', width: 12 },
          { header: 'Operação', key: 'numero_operacao', width: 12 },
          { header: 'Cliente', key: 'cliente', width: 35 },
          { header: 'CNPJ Cliente', key: 'cliente_cnpj', width: 18 },
          { header: 'Área', key: r => [r.area_codigo, r.area_nome].filter(Boolean).join(' - '), width: 22 },
          { header: 'Nº Pedido', key: 'numero_pedido', width: 18 },
          { header: 'Código Etiqueta', key: 'codigo_lido', width: 45 },
          { header: 'Volume', key: r => r.volume_atual ? `${r.volume_atual}${r.total_volumes ? '/' + r.total_volumes : ''}` : '', width: 10 },
          { header: 'Peso', key: 'peso', width: 10 },
          { header: 'Status', key: 'resultado', width: 12 },
          { header: 'Tipo Leitura', key: 'tipo_leitura', width: 14 },
          { header: 'Conferente', key: 'conferente_nome', width: 24 },
          { header: 'Data/Hora', key: r => r.criado_em ? new Date(r.criado_em).toLocaleString('pt-BR') : '', width: 18 },
          { header: 'Status Palete', key: 'status_palete', width: 14 },
          { header: 'Status Operação', key: 'status_operacao', width: 14 }
        ],
        fileName: `relatorio-conferencia-${filtroDataInicio}-a-${filtroDataFim}${nomeCliente ? '-' + nomeCliente.substring(0, 20).replace(/\s/g, '_') : ''}`,
        sheetName: 'Conferencia'
      });
    } catch (err: any) {
      setErro(err?.message || 'Erro ao exportar para Excel');
    } finally {
      setExportando(null);
    }
  };

  const handleImprimirPDF = async () => {
    try {
      setExportando('pdf');
      setErro('');
      const dados = await buscarDadosExport();
      const buscaAtiva = Boolean(
        (buscaApplied && buscaApplied.trim()) || filtroNumeroPedidoApplied.trim()
      );
      if (dados.length === 0) {
        setErro(buscaAtiva
          ? 'Nenhum dado encontrado para os filtros de busca atuais.'
          : `Nenhuma leitura encontrada no periodo de ${filtroDataInicio} a ${filtroDataFim}. Tente ampliar o intervalo de datas.`);
        return;
      }
      const nomeCliente = filtroCliente
        ? (clientesOptions.find(c => String(c.id) === String(filtroCliente))?.razao_social || '')
        : '';
      imprimirRelatorioConferencia({
        data: dados,
        periodoInicio: buscaAtiva ? undefined : filtroDataInicio,
        periodoFim: buscaAtiva ? undefined : filtroDataFim,
        filtros: {
          cliente: nomeCliente || undefined,
          numero_pedido: filtroNumeroPedidoApplied.trim() || undefined,
          busca: buscaApplied || undefined
        }
      });
    } catch (err: any) {
      setErro(err?.message || 'Erro ao gerar PDF');
    } finally {
      setExportando(null);
    }
  };

  const limparFiltros = () => {
    setBusca('');
    setBuscaApplied('');
    setFiltroCliente('');
    setFiltroNumeroPedido('');
    setFiltroNumeroPedidoApplied('');
    setFiltroDataInicio(getHojeISO());
    setFiltroDataFim(getHojeISO());
    setPage(1);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          <i className="fas fa-file-alt" style={{ marginRight: 8 }}></i>
          Relatórios de Conferência
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-success"
            onClick={handleExportarExcel}
            disabled={exportando !== null}
            title="Exportar para Excel"
          >
            <i className={`fas ${exportando === 'excel' ? 'fa-spinner fa-spin' : 'fa-file-excel'}`} style={{ marginRight: 6 }}></i>
            {exportando === 'excel' ? 'Exportando...' : 'Excel'}
          </button>
          <button
            className="btn"
            onClick={handleImprimirPDF}
            disabled={exportando !== null}
            title="Imprimir / Salvar como PDF"
            style={{ background: '#e74c3c', color: '#fff' }}
          >
            <i className={`fas ${exportando === 'pdf' ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`} style={{ marginRight: 6 }}></i>
            {exportando === 'pdf' ? 'Gerando...' : 'PDF'}
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/conferencia')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
        </div>
      </div>

      {/* Mensagem de Erro */}
      {erro && (
        <div className="montagem-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-exclamation-circle"></i>
          <span>{erro}</span>
          <button onClick={() => setErro('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1rem' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="page-content">
        {/* Filtros */}
        <div className="montagem-filters">
          <div className="filter-input">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar por nº palete, cliente ou área..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') aplicarFiltros(); }}
            />
          </div>
          <select value={filtroCliente} onChange={(e) => { setFiltroCliente(e.target.value); setPage(1); }}>
            <option value="">Todos os clientes</option>
            {clientesOptions.map(c => (
              <option key={c.id} value={c.id}>{c.razao_social}</option>
            ))}
          </select>
          <input
            type="date"
            value={filtroDataInicio}
            onChange={(e) => { setFiltroDataInicio(e.target.value); setPage(1); }}
            title="Data inicial (conferência)"
          />
          <input
            type="date"
            value={filtroDataFim}
            onChange={(e) => { setFiltroDataFim(e.target.value); setPage(1); }}
            title="Data final (conferência)"
          />
          <div className="filter-input">
            <i className="fas fa-file-invoice"></i>
            <input
              type="text"
              placeholder="Nº do pedido..."
              value={filtroNumeroPedido}
              onChange={(e) => setFiltroNumeroPedido(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') aplicarFiltros(); }}
              title="Filtrar por número do pedido"
            />
          </div>
          <button className="btn btn-primary" onClick={aplicarFiltros} title="Pesquisar">
            <i className="fas fa-search" style={{ marginRight: 6 }}></i>Pesquisar
          </button>
          <button className="btn btn-secondary" onClick={limparFiltros} title="Limpar filtros">
            <i className="fas fa-eraser" style={{ marginRight: 6 }}></i>Limpar
          </button>
        </div>

        {/* Tabela */}
        <div className="card">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
              <div className="loading-spinner-large"></div>
              <p>Carregando relatório...</p>
            </div>
          ) : paletes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
              <i className="fas fa-file-alt" style={{ fontSize: '3rem', color: '#ddd', marginBottom: 16, display: 'block' }}></i>
              <p>Nenhum palete encontrado no período selecionado.</p>
              <p style={{ fontSize: '0.85rem' }}>Ajuste as datas ou filtros para visualizar mais resultados.</p>
            </div>
          ) : (
            <>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #ecf0f1', fontSize: '0.85rem', color: '#7f8c8d' }}>
                <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
                Exibindo {paletes.length} de {total} palete(s). Clique em uma linha para ver pedidos e etiquetas bipadas.
              </div>
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Nº Palete</th>
                      <th>Cliente</th>
                      <th>Operação</th>
                      <th>Área de Entrega</th>
                      <th>Pedidos</th>
                      <th>Volumes</th>
                      <th>Peso (kg)</th>
                      <th>Status Palete</th>
                      <th>Status Op.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paletes.map((p) => (
                      <React.Fragment key={p.id}>
                        <tr
                          style={{ cursor: 'pointer', background: expandedPalete === p.id ? '#f0f7ff' : undefined }}
                          onClick={() => handleExpandPalete(p.id)}
                        >
                          <td style={{ textAlign: 'center' }}>
                            <i className={`fas fa-chevron-${expandedPalete === p.id ? 'down' : 'right'}`} style={{ color: '#7f8c8d', fontSize: '0.8rem' }}></i>
                          </td>
                          <td style={{ fontWeight: 700 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <i className="fas fa-pallet" style={{ color: '#8e44ad' }}></i>
                              #{p.numero_palete}
                            </span>
                          </td>
                          <td>{p.cliente_razao_social || '-'}</td>
                          <td>
                            <span
                              style={{ color: '#3498db', cursor: 'pointer', fontWeight: 500 }}
                              onClick={(e) => { e.stopPropagation(); navigate(`/montar-carga/${p.operacao_id}`); }}
                            >
                              #{p.numero_operacao}
                            </span>
                          </td>
                          <td>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <i className="fas fa-map-marker-alt" style={{ color: '#7f8c8d' }}></i>
                              {p.area_codigo && p.area_nome ? `${p.area_codigo} (${p.area_nome})` : (p.area_codigo || p.area_nome || '-')}
                            </span>
                          </td>
                          <td><span style={{ fontWeight: 600 }}>{p.total_pedidos}</span></td>
                          <td>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontWeight: 600 }}>{p.leituras_ok}</span>
                              {p.leituras_duplicadas > 0 && (
                                <span style={{ fontSize: '0.75rem', color: '#e67e22', background: '#fef3e8', padding: '2px 6px', borderRadius: 4 }}>
                                  +{p.leituras_duplicadas} dup
                                </span>
                              )}
                            </span>
                          </td>
                          <td>{Number(p.peso_total).toFixed(1)}</td>
                          <td>
                            {(() => {
                              const paleteStatus = p.status || 'pendente';
                              const cfg = STATUS_PALETE_COLORS[paleteStatus] || { color: '#555', bg: '#eee' };
                              return (
                                <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, background: cfg.bg, color: cfg.color }}>
                                  {STATUS_PALETE_LABELS[paleteStatus] || paleteStatus}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <span className={`status-badge status-montagem-${p.operacao_status}`} style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
                              {STATUS_LABELS[p.operacao_status] || p.operacao_status}
                            </span>
                          </td>
                        </tr>

                        {/* Linha expandida com detalhes */}
                        {expandedPalete === p.id && (
                          <tr>
                            <td colSpan={10} style={{ padding: 0, background: '#fafbfc' }}>
                              {loadingDetalhe ? (
                                <div style={{ textAlign: 'center', padding: 30, color: '#7f8c8d' }}>
                                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
                                  Carregando detalhes...
                                </div>
                              ) : paleteDetalhado ? (
                                <PaleteDetalheExpanded
                                  palete={paleteDetalhado}
                                  expandedPedido={expandedPedido}
                                  onExpandPedido={handleExpandPedido}
                                  formatDate={formatDate}
                                />
                              ) : (
                                <div style={{ textAlign: 'center', padding: 20, color: '#e74c3c' }}>
                                  Erro ao carregar detalhes
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '16px 0' }}>
                  <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ padding: '6px 14px' }}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                    Página {page} de {totalPages}
                  </span>
                  <button className="btn btn-outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ padding: '6px 14px' }}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================
// Componente de detalhe expandido do palete
// =============================================

interface PaleteDetalheExpandedProps {
  palete: PaleteDetalhado;
  expandedPedido: string | null;
  onExpandPedido: (numeroPedido: string) => void;
  formatDate: (d: string) => string;
}

const PaleteDetalheExpanded: React.FC<PaleteDetalheExpandedProps> = ({ palete, expandedPedido, onExpandPedido, formatDate }) => {
  const resumo = palete.resumo;

  return (
    <div style={{ padding: '16px 24px 20px' }}>
      {/* Resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        <InfoChip icon="fas fa-barcode" label="Volumes OK" value={resumo.leituras_ok} color="#27ae60" />
        <InfoChip icon="fas fa-clone" label="Duplicados" value={resumo.leituras_duplicadas} color="#e67e22" />
        <InfoChip icon="fas fa-trash" label="Removidos" value={resumo.leituras_removidas} color="#95a5a6" />
        <InfoChip icon="fas fa-weight-hanging" label="Peso Total" value={`${resumo.peso_total} kg`} color="#3498db" />
        <InfoChip icon="fas fa-file-invoice" label="Pedidos" value={resumo.total_pedidos} color="#8e44ad" />
        {palete.ocorrencias.length > 0 && (
          <InfoChip icon="fas fa-exclamation-triangle" label="Ocorrências" value={palete.ocorrencias.length} color="#e74c3c" />
        )}
      </div>

      {/* Pedidos */}
      {palete.pedidos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#7f8c8d', fontSize: '0.9rem' }}>
          <i className="fas fa-inbox" style={{ marginRight: 8 }}></i>
          Nenhuma leitura registrada neste palete
        </div>
      ) : (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: '#f5f6fa', padding: '10px 16px', fontWeight: 600, fontSize: '0.85rem', color: '#2c3e50', borderBottom: '1px solid #e0e0e0' }}>
            <i className="fas fa-file-invoice" style={{ marginRight: 8, color: '#7f8c8d' }}></i>
            Pedidos ({palete.pedidos.length})
          </div>
          {palete.pedidos.map((pedido) => (
            <PedidoRow
              key={pedido.numero_pedido}
              pedido={pedido}
              etiquetasPendentes={(palete.etiquetas_pendentes || []).filter(
                ep => ep.numero_pedido === pedido.numero_pedido
              )}
              isExpanded={expandedPedido === pedido.numero_pedido}
              onToggle={() => onExpandPedido(pedido.numero_pedido)}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================
// Componente de linha do pedido (expandível)
// =============================================

interface PedidoRowProps {
  pedido: PaleteDetalhadoPedido;
  etiquetasPendentes: PaleteDetalhadaEtiquetaPendente[];
  isExpanded: boolean;
  onToggle: () => void;
  formatDate: (d: string) => string;
}

const PedidoRow: React.FC<PedidoRowProps> = ({ pedido, etiquetasPendentes, isExpanded, onToggle, formatDate }) => {
  const pendentes = pedido.total_volumes_esperado > 0
    ? Math.max(0, pedido.total_volumes_esperado - pedido.total_lido - calcularRemovidosUnicos(pedido.etiquetas))
    : 0;
  const percentual = pedido.total_volumes_esperado > 0
    ? Math.round((pedido.total_lido / pedido.total_volumes_esperado) * 100)
    : null;

  return (
    <>
      <div
        style={{
          padding: '10px 16px', borderBottom: '1px solid #e0e0e0', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.85rem',
          background: isExpanded ? '#f8f9ff' : '#fff', flexWrap: 'wrap'
        }}
        onClick={onToggle}
      >
        <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ color: '#7f8c8d', fontSize: '0.75rem', width: 12 }}></i>
        <span style={{ fontWeight: 600, color: '#2c3e50', minWidth: 100 }}>
          <i className="fas fa-file-invoice" style={{ marginRight: 6, color: '#8e44ad' }}></i>
          {pedido.numero_pedido}
        </span>
        <span style={{ color: '#27ae60', fontSize: '0.75rem' }}>
          <i className="fas fa-check-circle" style={{ marginRight: 3 }}></i>
          {pedido.total_lido} bipado{pedido.total_lido !== 1 ? 's' : ''}
        </span>
        {pendentes > 0 && (
          <span style={{ color: '#e65100', fontSize: '0.75rem', background: '#fff3e0', padding: '2px 8px', borderRadius: 12 }}>
            <i className="fas fa-hourglass-half" style={{ marginRight: 3 }}></i>
            {pendentes} pendente{pendentes !== 1 ? 's' : ''}
          </span>
        )}
        {pedido.total_volumes_esperado > 0 && (
          <span style={{ color: '#7f8c8d', fontSize: '0.72rem' }}>de {pedido.total_volumes_esperado}</span>
        )}
        {percentual !== null && (
          <span style={{
            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12,
            background: percentual >= 100 ? '#d4edda' : percentual >= 50 ? '#fff3cd' : '#fdeaea',
            color: percentual >= 100 ? '#155724' : percentual >= 50 ? '#856404' : '#721c24'
          }}>
            {percentual}%
          </span>
        )}
        {pedido.total_duplicados > 0 && (
          <span style={{ color: '#e67e22', fontSize: '0.75rem', background: '#fef3e8', padding: '2px 8px', borderRadius: 12 }}>
            {pedido.total_duplicados} dup
          </span>
        )}
        {pendentes === 0 && pedido.total_volumes_esperado > 0 && (
          <span style={{ color: '#155724', fontSize: '0.75rem', background: '#d4edda', padding: '2px 8px', borderRadius: 12 }}>
            <i className="fas fa-check-double" style={{ marginRight: 3 }}></i>Completo
          </span>
        )}
      </div>

      {/* Etiquetas do pedido */}
      {isExpanded && (() => {
        const todasEtiquetas: Array<{
          id: number | string;
          codigo: string;
          resultado: string;
          tipo_leitura: string;
          data: string;
          conferente: string;
          volume_atual: number | null;
          total_volumes: number | null;
          tipo: 'bipada' | 'pendente';
        }> = [];

        pedido.etiquetas.forEach((etiq: PaleteDetalhadoEtiqueta) => {
          const dados = etiq.dados_decodificados as Record<string, unknown> || {};
          const volKeys = Object.keys(dados);
          let volAtual: number | null = null;
          const rawVol = dados.volume_atual ?? dados['Volume Atual'] ?? dados['Volume']
            ?? dados[volKeys.find(k => /volume/i.test(k) && !/total/i.test(k)) || ''];
          if (rawVol != null) { const parsed = parseInt(String(rawVol)); if (!isNaN(parsed)) volAtual = parsed; }

          let totalVol: number | null = null;
          const rawTotal = dados.total_volumes ?? dados['Total de Volumes'] ?? dados['Total Volumes']
            ?? dados[volKeys.find(k => /total/i.test(k) && /volume/i.test(k)) || ''];
          if (rawTotal != null) { const parsed = parseInt(String(rawTotal)); if (!isNaN(parsed)) totalVol = parsed; }

          todasEtiquetas.push({
            id: etiq.id,
            codigo: etiq.codigo_lido,
            resultado: etiq.resultado,
            tipo_leitura: etiq.tipo_leitura,
            data: etiq.lido_em || etiq.criado_em,
            conferente: etiq.conferente_nome || '-',
            volume_atual: volAtual,
            total_volumes: totalVol,
            tipo: 'bipada',
          });
        });

        etiquetasPendentes.forEach(etq => {
          todasEtiquetas.push({
            id: `pend-${etq.id}`,
            codigo: etq.codigo_barras || '',
            resultado: 'pendente',
            tipo_leitura: '-',
            data: '',
            conferente: '-',
            volume_atual: etq.volume_atual,
            total_volumes: etq.total_volumes_pedido,
            tipo: 'pendente',
          });
        });

        todasEtiquetas.sort((a, b) => {
          const va = a.volume_atual ?? 9999;
          const vb = b.volume_atual ?? 9999;
          if (va !== vb) return va - vb;
          if (a.tipo !== b.tipo) return a.tipo === 'bipada' ? -1 : 1;
          return 0;
        });

        return (
          <div style={{ background: '#f8f9ff', borderBottom: '1px solid #e0e0e0', overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#eef1f8' }}>
                  <th style={{ padding: '6px 16px 6px 44px', textAlign: 'left', fontWeight: 600, color: '#7f8c8d' }}>Código</th>
                  <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#7f8c8d' }}>Volume</th>
                  <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#7f8c8d' }}>Status</th>
                  <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#7f8c8d' }}>Tipo</th>
                  <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#7f8c8d' }}>Conferente</th>
                  <th style={{ padding: '6px 16px', textAlign: 'left', fontWeight: 600, color: '#7f8c8d' }}>Data/Hora</th>
                </tr>
              </thead>
              <tbody>
                {todasEtiquetas.map((etiq) => {
                  const res = RESULTADO_LABELS[etiq.resultado] || { label: etiq.resultado, color: '#555', icon: 'fas fa-circle' };
                  return (
                    <tr key={etiq.id} style={{ borderBottom: '1px solid #eee', background: etiq.tipo === 'pendente' ? '#fff8e1' : undefined }}>
                      <td style={{ padding: '6px 16px 6px 44px', fontFamily: 'monospace', fontSize: '0.78rem', color: etiq.tipo === 'pendente' ? '#333' : '#2c3e50' }}>
                        {etiq.codigo
                          ? (etiq.codigo.length > 45 ? etiq.codigo.substring(0, 45) + '...' : etiq.codigo)
                          : `Volume ${etiq.volume_atual || '?'} de ${etiq.total_volumes || '?'}`
                        }
                      </td>
                      <td style={{ padding: '6px 12px', fontSize: '0.78rem', color: '#555' }}>
                        {etiq.volume_atual ? `${etiq.volume_atual}${etiq.total_volumes ? `/${etiq.total_volumes}` : ''}` : '-'}
                      </td>
                      <td style={{ padding: '6px 12px' }}>
                        <span style={{ color: res.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <i className={res.icon} style={{ fontSize: '0.7rem' }}></i>
                          {res.label}
                        </span>
                      </td>
                      <td style={{ padding: '6px 12px', color: '#7f8c8d' }}>{etiq.tipo_leitura}</td>
                      <td style={{ padding: '6px 12px', color: '#2c3e50', fontSize: '0.78rem' }}>
                        <i className="fas fa-user" style={{ marginRight: 4, color: '#7f8c8d', fontSize: '0.7rem' }}></i>
                        {etiq.conferente}
                      </td>
                      <td style={{ padding: '6px 16px', color: '#95a5a6' }}>{etiq.data ? formatDate(etiq.data) : '-'}</td>
                    </tr>
                  );
                })}
                {todasEtiquetas.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: '#95a5a6', padding: 12 }}>
                      Nenhuma etiqueta encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}
    </>
  );
};

// =============================================
// Chip de informação
// =============================================

const InfoChip: React.FC<{ icon: string; label: string; value: string | number; color: string }> = ({ icon, label, value, color }) => (
  <div style={{
    background: '#fff', border: '1px solid #ecf0f1', borderRadius: 8, padding: '10px 14px',
    display: 'flex', alignItems: 'center', gap: 10
  }}>
    <i className={icon} style={{ color, fontSize: '1rem' }}></i>
    <div>
      <div style={{ fontSize: '0.7rem', color: '#7f8c8d', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2c3e50' }}>{value}</div>
    </div>
  </div>
);

export default RelatoriosPage;
